'use client';

import * as React from 'react';
import Link from 'next/link';
import { MapPin, ArrowUpRight } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { buildLocalizedNavHref } from '@/components/layout/Header/nav/navActive';
import { stripHtml } from '@/lib/homepage-mappers';
import { getStrapiFormatUrl } from '@/lib/strapi-media-url';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { CompanyProfileInfraProjects } from '@/types/company-profile';

gsap.registerPlugin(ScrollTrigger);

type MarvelProject = {
  id: number;
  title: string;
  location: string;
  shortDescription: string;
  imageUrl: string | null;
};

interface CompanyProfileMarvelsProps {
  locale: string;
  zone: CompanyProfileInfraProjects;
}

function buildMarvelItems(zone: CompanyProfileInfraProjects): MarvelProject[] {
  return (zone.projectAccordion ?? [])
    .map((item) => ({
      id: item.id,
      title: item.projectTitle?.trim() || '',
      location: item.locationName?.trim() || '',
      shortDescription: item.projectDescription ? stripHtml(item.projectDescription) : '',
      imageUrl: getStrapiFormatUrl(item.media),
    }))
    .filter((item) => item.title);
}

function MarvelProjectImage({
  project,
  active,
  priority,
  sizes,
}: {
  project: MarvelProject;
  active: boolean;
  priority: boolean;
  sizes: string;
}) {
  return (
    <div
      className="absolute inset-0 transition-opacity duration-500 ease-in-out"
      style={{ opacity: active ? 1 : 0 }}
    >
      {project.imageUrl ? (
        <CmsImage
          src={project.imageUrl}
          alt={project.title}
          fill
          sizes={sizes}
          className="object-cover"
          priority={priority}
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
      ) : (
        <CmsImage
          src={MEDIA_PLACEHOLDER}
          alt={project.title}
          fill
          sizes={sizes}
          className="object-cover"
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
    </div>
  );
}

/** Build the scroll-triggered reveal timeline for the marvels section. */
function buildMarvelsRevealTimeline(section: HTMLElement, rule: HTMLSpanElement | null) {
  const tl = gsap.timeline({
    scrollTrigger: { trigger: section, start: 'top 75%', toggleActions: 'play none none none' },
  });

  tl.fromTo(
    '[data-mv-eyebrow]',
    { autoAlpha: 0, y: 12 },
    { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' },
  )
    .fromTo(
      '[data-mv-line]',
      { y: '108%' },
      { y: '0%', stagger: 0.1, duration: 0.78, ease: 'power3.out' },
      '-=0.25',
    )
    .fromTo(
      rule,
      { scaleX: 0, transformOrigin: 'left center' },
      { scaleX: 1, duration: 0.55, ease: 'power2.inOut' },
      '-=0.4',
    )
    .fromTo(
      '[data-mv-sub]',
      { autoAlpha: 0, y: 10 },
      { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' },
      '-=0.3',
    )
    .fromTo(
      '[data-mv-img]',
      { autoAlpha: 0, x: 30 },
      { autoAlpha: 1, x: 0, duration: 0.8, ease: 'power3.out' },
      '-=0.3',
    )
    .fromTo(
      '[data-mv-row]',
      { autoAlpha: 0, x: -20 },
      { autoAlpha: 1, x: 0, stagger: 0.1, duration: 0.6, ease: 'power2.out' },
      '-=0.65',
    )
    .fromTo(
      '[data-mv-cta]',
      { autoAlpha: 0, y: 20, rotateX: 12 },
      { autoAlpha: 1, y: 0, rotateX: 5, duration: 0.6, ease: 'power2.out' },
      '-=0.2',
    );
}

function MarvelListRow({
  project,
  index,
  isActive,
  setHovered,
  rowRef,
}: {
  project: MarvelProject;
  index: number;
  isActive: boolean;
  setHovered: (index: number) => void;
  rowRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      data-mv-row
      ref={rowRef}
      className="border-b border-white/[0.06] first:border-t first:border-white/[0.06]"
      style={{ opacity: 0 }}
      onMouseEnter={() => setHovered(index)}
      onFocus={() => setHovered(index)}
      onTouchStart={() => setHovered(index)}
    >
      <div className="flex items-start gap-4 py-6 pl-4 transition-colors duration-200 hover:bg-white/[0.02] sm:gap-6 sm:py-9 sm:pl-6 md:pl-8 md:py-11">
        <span
          className={`shrink-0 font-mono text-[1.35rem] font-black leading-none tabular-nums transition-colors duration-300 sm:text-[2.5rem] ${
            isActive ? 'text-white/30' : 'text-white/[0.06]'
          }`}
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        <div className="min-w-0 flex-1">
          {project.location ? (
            <p className="mb-2.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#ed1c22]">
              <MapPin className="h-2.5 w-2.5 shrink-0" aria-hidden />
              {project.location}
            </p>
          ) : null}
          <h3
            className={`text-[1.05rem] font-semibold leading-snug tracking-tight transition-colors duration-200 sm:text-[1.4rem] lg:text-[1.6rem] ${
              isActive ? 'text-white' : 'text-white/55'
            }`}
          >
            {project.title}
          </h3>
          {project.shortDescription ? (
            <p className="mt-3 max-w-[520px] text-[13px] leading-relaxed text-white/35 sm:text-sm">
              {project.shortDescription}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MarvelExploreCta({
  exploreHref,
  exploreEyebrow,
  exploreText,
  ctaHovered,
  setCtaHovered,
}: {
  exploreHref: string;
  exploreEyebrow: string;
  exploreText: string;
  ctaHovered: boolean;
  setCtaHovered: (next: boolean) => void;
}) {
  return (
    <Link
      href={exploreHref}
      data-mv-cta
      className="group mt-8 flex cursor-pointer items-center justify-between gap-4 overflow-hidden rounded-2xl bg-[#ed1c22] px-5 py-5 no-underline sm:mt-10 sm:px-7 sm:py-6"
      style={{
        opacity: 0,
        transformStyle: 'preserve-3d',
        transform: ctaHovered
          ? 'perspective(700px) rotateX(0deg) translateY(-5px)'
          : 'perspective(700px) rotateX(5deg)',
        boxShadow: ctaHovered
          ? '0 36px 60px -8px rgba(237,28,34,0.6), 0 14px 30px rgba(0,0,0,0.4)'
          : '0 20px 44px -6px rgba(237,28,34,0.38), 0 8px 20px rgba(0,0,0,0.3)',
        transition:
          'transform 0.35s cubic-bezier(0.4,0,0.2,1), box-shadow 0.35s ease, opacity 0.5s ease',
      }}
      onMouseEnter={() => setCtaHovered(true)}
      onMouseLeave={() => setCtaHovered(false)}
      aria-label={exploreText}
    >
      <div>
        {exploreEyebrow ? (
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/55">
            {exploreEyebrow}
          </p>
        ) : null}
        <p className="mt-1.5 text-sm font-bold text-white sm:text-lg">{exploreText}</p>
      </div>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-all duration-300 group-hover:bg-white group-hover:text-[#ed1c22] sm:h-12 sm:w-12">
        <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </Link>
  );
}

function MarvelDesktopPanel({
  items,
  hovered,
  setHovered,
}: {
  items: MarvelProject[];
  hovered: number;
  setHovered: (index: number) => void;
}) {
  return (
    <div
      data-mv-img
      className="relative hidden shrink-0 overflow-hidden rounded-3xl lg:block lg:w-[42%] xl:w-[46%]"
      style={{ opacity: 0 }}
    >
      {items.map((project, i) => (
        <div
          key={project.id}
          className="absolute inset-0 transition-opacity duration-500 ease-in-out"
          style={{ opacity: hovered === i ? 1 : 0 }}
        >
          {project.imageUrl ? (
            <CmsImage
              src={project.imageUrl}
              alt={project.title}
              fill
              sizes="(min-width: 1280px) 600px, 45vw"
              className="object-cover"
              priority={i === 0}
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
          ) : (
            <CmsImage
              src={MEDIA_PLACEHOLDER}
              alt={project.title}
              fill
              sizes="(min-width: 1280px) 600px, 45vw"
              className="object-cover"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-transparent" />
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black/40 to-transparent" />
        </div>
      ))}

      <div className="absolute inset-x-0 bottom-0 z-10 p-8 lg:p-10">
        {items.map((project, i) => (
          <div
            key={project.id}
            className="transition-all duration-500 ease-out"
            style={{
              opacity: hovered === i ? 1 : 0,
              transform: hovered === i ? 'translateY(0)' : 'translateY(12px)',
            }}
          >
            {hovered === i && (
              <>
                {project.location ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ed1c22] px-3 py-1">
                    <MapPin className="h-2.5 w-2.5 text-white" aria-hidden />
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                      {project.location}
                    </span>
                  </span>
                ) : null}
                <p className="mt-4 text-[1.6rem] font-bold leading-[1.1] tracking-tight text-white [text-shadow:0_2px_24px_rgba(0,0,0,0.6)] sm:text-[2rem] lg:text-[2.2rem]">
                  {project.title}
                </p>
                {project.shortDescription ? (
                  <p className="mt-2.5 text-[13px] leading-relaxed text-white/55 [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]">
                    {project.shortDescription}
                  </p>
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>

      <div className="absolute left-7 top-7 z-10">
        <span className="font-mono text-[11px] text-white/30">
          {String(hovered + 1).padStart(2, '0')}&thinsp;/&thinsp;
          {String(items.length).padStart(2, '0')}
        </span>
      </div>

      <div className="absolute right-6 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-2">
        {items.map((project, i) => (
          <button
            key={project.id}
            type="button"
            onClick={() => setHovered(i)}
            className={`rounded-full transition-all duration-300 ${
              hovered === i ? 'h-6 w-1.5 bg-white' : 'h-1.5 w-1.5 bg-white/25 hover:bg-white/50'
            }`}
            aria-label={`View project ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function MarvelsHeader({
  zone,
  headingFirst,
  headingLast,
  uspLabels,
  ruleRef,
}: {
  zone: CompanyProfileInfraProjects;
  headingFirst: string;
  headingLast: string;
  uspLabels: string[];
  ruleRef: React.RefObject<HTMLSpanElement>;
}) {
  return (
    <div className="mb-8 sm:mb-10 md:mb-12">
      {zone.eyebrowHeading ? (
        <div data-mv-eyebrow className="mb-6 inline-flex items-center gap-3" style={{ opacity: 0 }}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#ed1c22]">
            {zone.eyebrowHeading}
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-6">
        {headingFirst || headingLast ? (
          <h2 className="text-[1.85rem] font-bold leading-[1.08] tracking-tight sm:text-[3rem] lg:text-[3.8rem]">
            {headingFirst ? (
              <span className="block overflow-hidden">
                <span className="block" data-mv-line>
                  {headingFirst}
                </span>
              </span>
            ) : null}
            {headingLast ? (
              <span className="block overflow-hidden">
                <span className="block text-white" data-mv-line>
                  {headingLast}
                </span>
              </span>
            ) : null}
          </h2>
        ) : null}

        <div
          data-mv-sub
          className="max-w-[360px] border-l-2 border-[#ed1c22]/40 pl-4 sm:mb-1 sm:pl-5"
          style={{ opacity: 0 }}
        >
          {zone.subHeading ? (
            <p className="text-[15px] font-medium leading-relaxed text-white/75">
              {stripHtml(zone.subHeading)}
            </p>
          ) : null}
          {uspLabels.length > 0 ? (
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/25">
              {uspLabels.join('  ·  ')}
            </p>
          ) : null}
        </div>
      </div>

      <span
        ref={ruleRef}
        aria-hidden
        className="mt-7 block h-[2px] w-full bg-white/[0.07]"
        style={{ transform: 'scaleX(0)', transformOrigin: 'left center' }}
      />
    </div>
  );
}

export default function CompanyProfileMarvels({ locale, zone }: CompanyProfileMarvelsProps) {
  const sectionRef = React.useRef<HTMLElement>(null);
  const ruleRef = React.useRef<HTMLSpanElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const rowRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [hovered, setHovered] = React.useState(0);
  const [ctaHovered, setCtaHovered] = React.useState(false);
  const [indicator, setIndicator] = React.useState({ top: 0, height: 0 });

  const items: MarvelProject[] = buildMarvelItems(zone);

  const uspLabels = (zone.headingUSP ?? [])
    .map((item) => item.listTitle?.trim())
    .filter((label): label is string => Boolean(label));

  const explore = zone.exploreButton?.[0];
  const exploreEyebrow = explore?.eyebrowHeading?.trim() || '';
  const exploreText = explore?.mainText?.trim() || '';
  const exploreHrefRaw = explore?.pageUrl?.trim() || '';
  const exploreHref = exploreHrefRaw ? buildLocalizedNavHref(exploreHrefRaw, locale) : '';
  const showExploreCta =
    Boolean(exploreText) && Boolean(exploreHref) && exploreHref !== '#' && exploreHref !== '#!';

  React.useLayoutEffect(() => {
    const row = rowRefs.current[hovered];
    const list = listRef.current;
    if (!row || !list) return;
    setIndicator({ top: row.offsetTop, height: row.offsetHeight });
  }, [hovered]);

  React.useLayoutEffect(() => {
    const section = sectionRef.current;
    const rule = ruleRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      buildMarvelsRevealTimeline(section, rule);
    }, section);

    return () => ctx.revert();
  }, []);

  if (items.length === 0) return null;

  const headingParts = (zone.heading?.trim() || '').split(/\s+/).filter(Boolean);
  const headingLast = headingParts.pop() || '';
  const headingFirst = headingParts.join(' ');

  return (
    <section ref={sectionRef} className="bg-transparent text-white">
      <div className="container mx-auto px-4 sm:px-8 lg:px-12">
        <MarvelsHeader
          zone={zone}
          headingFirst={headingFirst}
          headingLast={headingLast}
          uspLabels={uspLabels}
          ruleRef={ruleRef}
        />

        <div className="flex flex-col items-stretch gap-8 lg:flex-row lg:gap-12 xl:gap-16">
          <div
            data-mv-img
            className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl sm:aspect-[16/9] lg:hidden"
            style={{ opacity: 0 }}
          >
            {items.map((project, i) => (
              <MarvelProjectImage
                key={`mobile-${project.id}`}
                project={project}
                active={hovered === i}
                priority={i === 0}
                sizes="100vw"
              />
            ))}

            <div className="absolute inset-x-0 bottom-0 z-10 p-4 sm:p-5">
              {items[hovered]?.location ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ed1c22] px-3 py-1">
                  <MapPin className="h-2.5 w-2.5 text-white" aria-hidden />
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white">
                    {items[hovered]?.location}
                  </span>
                </span>
              ) : null}
              <p className="mt-3 text-lg font-bold leading-[1.15] tracking-tight text-white sm:text-xl">
                {items[hovered]?.title}
              </p>
            </div>
          </div>

          <div ref={listRef} className="relative min-w-0 flex-1">
            <span
              aria-hidden
              className="pointer-events-none absolute left-0 w-[3px] rounded-full bg-[#ed1c22]"
              style={{
                top: indicator.top,
                height: indicator.height,
                transition:
                  'top 0.38s cubic-bezier(0.4,0,0.2,1), height 0.38s cubic-bezier(0.4,0,0.2,1)',
              }}
            />

            {items.map((project, i) => (
              <MarvelListRow
                key={project.id}
                project={project}
                index={i}
                isActive={hovered === i}
                setHovered={setHovered}
                rowRef={(el) => {
                  rowRefs.current[i] = el;
                }}
              />
            ))}

            {showExploreCta ? (
              <MarvelExploreCta
                exploreHref={exploreHref}
                exploreEyebrow={exploreEyebrow}
                exploreText={exploreText}
                ctaHovered={ctaHovered}
                setCtaHovered={setCtaHovered}
              />
            ) : null}
          </div>

          <MarvelDesktopPanel items={items} hovered={hovered} setHovered={setHovered} />
        </div>
      </div>
    </section>
  );
}
